import { HStack, VStack } from '@chakra-ui/react';
import { FolderIcon } from 'Tokens/Icons/Data';
import { Accordion } from 'Atoms/Accordion/Accordion.tsx';
import { BusinessUnitFinancialTable } from './BusinessUnitFinancialTable.tsx';
import { useGroupFinancials } from './FinancialTables.hooks.ts';
import { Tag } from 'Atoms/Tag/Tag.tsx';
import { useTranslation } from 'utils/translation/translation.ts';
import { Typography } from 'Tokens/Typography/Typography.tsx';
import { BAssessment, ReportingGroup } from 'models/types.ts';
import { BusinessUnitsSortingType } from 'containers/Assessments/pieces/Assessment.hooks.ts';
import { useBuildTree } from 'Features/CompanyStructure/useBuildTree.tsx';
import { getDescendants } from '@minoru/react-dnd-treeview';

type GroupFinancialsProps = {
  reportingGroupId: string;
  bg: 'solid' | 'outlined';
  childrenCount?: number;
  reportingGroups: ReportingGroup[];
  bAssessments: BAssessment[];
  businessUnitsSorting: BusinessUnitsSortingType;
};

export const GroupFinancials = ({
  reportingGroupId,
  bg,
  childrenCount = 0,
  reportingGroups,
  bAssessments,
  businessUnitsSorting,
}: GroupFinancialsProps) => {
  const { reportingGroup, childGroups, groupBusinessUnits, isDone } =
    useGroupFinancials(reportingGroupId);
  const { t } = useTranslation();
  if (!reportingGroup) return null;

  // eslint-disable-next-line react-compiler/react-compiler
  const treeData: any[] = useBuildTree(
    reportingGroups,
    bAssessments,
    'company',
    '',
    businessUnitsSorting.sortAlphabetically,
    businessUnitsSorting.groupsFirst
  );

  return (
    <Accordion
      variant={bg}
      marginBottom="12px"
      title={reportingGroup.name}
      lazyLoadContent
      items={[
        {
          label: reportingGroup.name,
          icon: <FolderIcon marginTop="8px" />,
          accordionNb: childrenCount,
          tag:
            isDone !== undefined
              ? [
                  <Tag variant={isDone ? 'success' : 'default'}>
                    {isDone ? t('common:buttons.done') : t('common:buttons.todo')}
                  </Tag>,
                ]
              : [<Tag>{t('common:words.empty')}</Tag>],
          content: (
            <VStack alignItems="flex-start" bg={bg === 'solid' ? 'bg.muted' : 'white'}>
              {childGroups &&
                childGroups.map((rg) => (
                  <GroupFinancials
                    key={rg.id}
                    reportingGroupId={rg.id}
                    bg="outlined"
                    childrenCount={getDescendants(treeData, rg.id).length || 0}
                    reportingGroups={reportingGroups}
                    bAssessments={bAssessments}
                    businessUnitsSorting={businessUnitsSorting}
                  />
                ))}
              {groupBusinessUnits.length ? (
                groupBusinessUnits.map((bu) => (
                  <BusinessUnitFinancialTable key={bu.id} businessUnit={bu} />
                ))
              ) : (
                <>
                  {!childGroups.length && (
                    <HStack width="100%" justifyContent="center">
                      <Typography variant="bodyStrong">
                        {t('common:assessment.groupFinancials.reporting')}
                      </Typography>
                    </HStack>
                  )}
                </>
              )}
            </VStack>
          ),
        },
      ]}
    />
  );
};
